import { useEffect } from 'react';
import { toPng } from 'html-to-image';
import { useMermaidSvgRegistry } from '../context/MermaidSvgRegistryContext';
import { useTheme } from '../context/ThemeProvider';
import { getColorSchemePalette } from '../context/colorSchemes';

/**
 * useDiagramExport
 *
 * Captures a rendered diagram element as a PNG and registers it in the
 * MermaidSvgRegistry, making it available for PDF/DOCX message export.
 *
 * @param {React.RefObject} ref       - Ref pointing to the DOM element to capture
 * @param {string}          sourceKey - Raw source code string used as registry key
 * @param {object}          [options]
 * @param {number}          [options.delay=300]           - ms to wait before capturing (allow paint to settle)
 * @param {any}             [options.trigger]             - Extra dep that re-fires capture when it changes
 * @param {number}          [options.pixelRatio=2]        - Pixel density for toPng
 * @param {boolean}         [options.enabled=true]        - Set false to skip (e.g. still streaming)
 * @param {function}        [options.preCapture]          - fn(el) => extraToPngOptions — mutate element before capture
 * @param {function}        [options.postCapture]         - fn(el) — restore element after capture (called in finally)
 */
export function useDiagramExport(ref, sourceKey, {
  delay = 300,
  trigger,
  pixelRatio = 2,
  enabled = true,
  preCapture,
  postCapture,
} = {}) {
  const registry = useMermaidSvgRegistry();
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);

  // Use `trigger` as the primary re-capture dependency when provided;
  // otherwise fall back to `sourceKey` (re-captures on new source).
  const dep = trigger !== undefined ? trigger : sourceKey;

  useEffect(() => {
    if (!enabled || !sourceKey || !registry) return;

    const timer = setTimeout(() => {
      const el = ref.current;
      if (!el) return;

      const backgroundColor = palette.background;
      let extraOptions = {};

      if (preCapture) {
        extraOptions = preCapture(el) || {};
      }

      toPng(el, { cacheBust: true, pixelRatio, backgroundColor, skipFonts: true, ...extraOptions })
        .then((pngBase64) => {
          registry.registerDiagram(sourceKey, pngBase64);
        })
        .catch(() => {
          // Silently ignore — diagram simply won't appear in export
        })
        .finally(() => {
          if (postCapture) postCapture(el);
        });
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep, sourceKey, enabled, registry, palette]);
}
